import { type ReactNode } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { tooltipContent, tooltipArrow } from './tooltip.css';
import { wrapTextChild } from '../wrapTextChild';
import { tooltipDefaults } from './tooltipDefaults';

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipProps {
  /** Tooltip text/content. If falsy, no tooltip is shown. */
  content: ReactNode;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Which side to show the tooltip on */
  side?: TooltipSide;
  /** Show an arrow pointing to the trigger */
  arrow?: boolean;
  /** Delay before tooltip opens (ms) */
  delayDuration?: number;
  /** Controlled open state (optional; uncontrolled when omitted). */
  open?: boolean;
  /** Called when Radix wants to open or close (hover, focus, Esc, pointerdown). */
  onOpenChange?: (open: boolean) => void;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  arrow = false,
  delayDuration,
  open,
  onOpenChange,
}: TooltipProps) {
  const resolvedDelay = delayDuration ?? tooltipDefaults.delayDuration;
  if (!content) return <>{children}</>;

  const tooltip = (
    <RadixTooltip.Root delayDuration={resolvedDelay} open={open} onOpenChange={onOpenChange}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content side={side} className={tooltipContent} sideOffset={4}>
          {wrapTextChild(content)}
          {arrow && <RadixTooltip.Arrow className={tooltipArrow} />}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );

  // F8: self-provide a Provider so the tooltip renders even if no ancestor
  // TooltipProvider is mounted. Each tooltip gets its own provider with the
  // injectable defaults.
  return (
    <RadixTooltip.Provider
      delayDuration={resolvedDelay}
      skipDelayDuration={tooltipDefaults.skipDelayDuration}
    >
      {tooltip}
    </RadixTooltip.Provider>
  );
}

/**
 * App-level TooltipProvider — wraps Radix's Provider. Used by main.tsx
 * and test wrappers to share a single Provider across all tooltips.
 */
export function TooltipProvider({
  children,
  ...props
}: RadixTooltip.TooltipProviderProps) {
  return (
    <RadixTooltip.Provider {...props}>{children}</RadixTooltip.Provider>
  );
}
